import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Loader2, MapPin } from "lucide-react";

// Australia bounding box: minLon,minLat,maxLon,maxLat
const AU_BBOX = "112.0,-44.0,154.0,-10.0";

function parseFeature(f) {
  const p = f.properties || {};
  const line = [p.housenumber, p.street || p.name].filter(Boolean).join(" ");
  return {
    address: line || p.name || "",
    suburb: p.city || p.town || p.district || p.locality || p.county || "",
    postcode: p.postcode || "",
    state: p.state || "",
  };
}

function label(f) {
  const p = f.properties || {};
  return [
    [p.housenumber, p.street || p.name].filter(Boolean).join(" "),
    p.city || p.town || p.district,
    p.state,
    p.postcode,
  ].filter(Boolean).join(", ");
}

export default function AddressAutocomplete({ value, onChange, onSelect, testid = "address-autocomplete" }) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef();
  const boxRef = useRef();

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const query = (q) => {
    clearTimeout(timer.current);
    if (!q || q.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    timer.current = setTimeout(async () => {
      setLoading(true);
      try {
        const url = `https://photon.komoot.io/api/?q=${encodeURIComponent(q)}&limit=6&lang=en&bbox=${AU_BBOX}`;
        const res = await fetch(url);
        const data = await res.json();
        const feats = (data.features || []).filter((f) => f.properties?.countrycode === "AU");
        setSuggestions(feats);
        setOpen(feats.length > 0);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 350);
  };

  const handleChange = (e) => {
    const v = e.target.value;
    onChange(v);
    query(v);
  };

  const pick = (f) => {
    onSelect(parseFeature(f));
    setOpen(false);
    setSuggestions([]);
  };

  return (
    <div className="relative" ref={boxRef}>
      <div className="relative">
        <Input
          value={value}
          onChange={handleChange}
          onFocus={() => suggestions.length && setOpen(true)}
          data-testid={testid}
          placeholder="Start typing an address…"
          autoComplete="off"
        />
        {loading && (
          <Loader2 className="w-4 h-4 animate-spin text-muted-foreground absolute right-3 top-1/2 -translate-y-1/2" />
        )}
      </div>
      {open && (
        <div
          className="absolute z-50 mt-1 w-full bg-popover border border-border/70 rounded-sm shadow-lg max-h-64 overflow-y-auto"
          data-testid={`${testid}-suggestions`}
        >
          {suggestions.map((f, i) => (
            <button
              key={i}
              type="button"
              onClick={() => pick(f)}
              data-testid={`${testid}-option-${i}`}
              className="w-full text-left px-3 py-2 text-sm hover:bg-accent/60 transition-colors flex items-start gap-2"
            >
              <MapPin className="w-3.5 h-3.5 text-brand mt-0.5 shrink-0" />
              <span>{label(f)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
